import { fetchJson } from './apiClient';

const TOKEN_KEY = 'luma_access_token';
const listeners = new Set();

export const publicConfigError = null;
export const isPublicConfigValid = true;

const getToken = () => localStorage.getItem(TOKEN_KEY);

const setToken = (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
};

const authHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getAccessToken = () => getToken();

export const authFetch = (input, init = {}) => fetch(input, {
    ...init,
    headers: { ...init.headers, ...authHeaders() }
});

export const authFetchJson = (input, init = {}) => fetchJson(input, {
    ...init,
    headers: { ...init.headers, ...authHeaders() }
});

const emitAuthChange = (event, session) => {
    listeners.forEach((callback) => {
        try {
            callback(event, session);
        } catch (error) {
            console.warn('[Auth] Listener failed:', error);
        }
    });
};

const captureTokenFromHash = () => {
    if (typeof window === 'undefined' || !window.location.hash) return null;
    const params = new URLSearchParams(window.location.hash.slice(1));
    const token = params.get('access_token');
    if (!token) return null;

    setToken(token);
    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, '', cleanUrl);
    return token;
};

const runAuthRequest = async (path, body, { storeSession = false } = {}) => {
    try {
        const payload = await fetchJson(path, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders()
            },
            body: JSON.stringify(body || {})
        });

        if (storeSession && payload?.session?.access_token) {
            payload.session.user = payload.user || payload.session.user || null;
            setToken(payload.session.access_token);
            emitAuthChange('SIGNED_IN', payload.session);
        }

        return { data: payload, error: null };
    } catch (error) {
        return { data: null, error };
    }
};

class RailwayQueryBuilder {
    constructor(table) {
        this.table = table;
        this.filters = [];
        this.ordering = null;
        this.limitCount = null;
        this.singleMode = false;
        this.maybeSingleMode = false;
        this.method = 'GET';
        this.payload = null;
        this.conflictColumns = '';
        this.signal = null;
    }

    select() {
        this.method = this.method === 'POST' || this.method === 'PATCH' ? this.method : 'GET';
        return this;
    }

    insert(payload) {
        this.method = 'POST';
        this.payload = Array.isArray(payload) ? { rows: payload } : { row: payload };
        return this;
    }

    update(values) {
        this.method = 'PATCH';
        this.payload = { values };
        return this;
    }

    delete() {
        this.method = 'DELETE';
        return this;
    }

    upsert(payload, options = {}) {
        this.method = 'POST';
        this.payload = Array.isArray(payload) ? { rows: payload } : { row: payload };
        this.conflictColumns = options.onConflict || '';
        return this;
    }

    eq(column, value) {
        this.filters.push({ op: 'eq', column, value });
        return this;
    }

    neq(column, value) {
        this.filters.push({ op: 'neq', column, value });
        return this;
    }

    in(column, value) {
        this.filters.push({ op: 'in', column, value });
        return this;
    }

    order(column, options = {}) {
        this.ordering = { column, ascending: options.ascending !== false };
        return this;
    }

    limit(count) {
        this.limitCount = count;
        return this;
    }

    single() {
        this.singleMode = true;
        return this;
    }

    maybeSingle() {
        this.maybeSingleMode = true;
        return this;
    }

    abortSignal(signal) {
        this.signal = signal;
        return this;
    }

    async execute() {
        const params = new URLSearchParams();
        if (this.filters.length) params.set('filters', JSON.stringify(this.filters));
        if (this.ordering) {
            params.set('order', this.ordering.column);
            params.set('ascending', String(this.ordering.ascending));
        }
        if (this.limitCount) params.set('limit', String(this.limitCount));
        if (this.singleMode) params.set('single', 'true');
        if (this.maybeSingleMode) params.set('maybeSingle', 'true');
        if (this.conflictColumns) params.set('onConflict', this.conflictColumns);

        const url = `/api/db/${this.table}${params.toString() ? `?${params}` : ''}`;
        try {
            const payload = await fetchJson(url, {
                method: this.method,
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders()
                },
                body: ['POST', 'PATCH'].includes(this.method) ? JSON.stringify(this.payload || {}) : undefined,
                signal: this.signal
            });
            let data = payload?.data ?? null;
            if ((this.singleMode || this.maybeSingleMode) && Array.isArray(data)) {
                data = data[0] || null;
            }
            return { data, error: null };
        } catch (error) {
            return { data: null, error };
        }
    }

    then(resolve, reject) {
        return this.execute().then(resolve, reject);
    }
}

export const supabase = {
    auth: {
        getSession: async () => {
            captureTokenFromHash();
            const token = getToken();
            if (!token) return { data: { session: null }, error: null };

            try {
                const payload = await fetchJson('/api/auth/session', {
                    headers: authHeaders()
                });
                return {
                    data: {
                        session: {
                            access_token: token,
                            user: payload.user
                        }
                    },
                    error: null
                };
            } catch (error) {
                setToken(null);
                return { data: { session: null }, error };
            }
        },
        getUser: async (token = getToken()) => {
            if (token && token !== getToken()) setToken(token);
            const { data, error } = await supabase.auth.getSession();
            return { data: { user: data.session?.user || null }, error };
        },
        signInWithPassword: ({ email, password }) => runAuthRequest('/api/auth/login', { email, password }, { storeSession: true }),
        signOut: async () => {
            setToken(null);
            emitAuthChange('SIGNED_OUT', null);
            return { error: null };
        },
        updateUser: (payload) => runAuthRequest('/api/auth/update-user', payload),
        onAuthStateChange: (callback) => {
            listeners.add(callback);
            return {
                data: {
                    subscription: {
                        unsubscribe: () => listeners.delete(callback)
                    }
                }
            };
        }
    },
    from: (table) => new RailwayQueryBuilder(table),
    rpc: async (name, params = {}) => {
        if (name !== 'check_email_exists') {
            return { data: null, error: new Error(`RPC non supportée: ${name}`) };
        }
        try {
            const payload = await fetchJson('/api/auth/check-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params)
            });
            return { data: Boolean(payload.exists), error: null };
        } catch (error) {
            return { data: null, error };
        }
    }
};
